const TASK_SLOT_COUNT = 20;

export interface TaskCell {
  id?: number;
  serTitle?: unknown;
  serDescription?: unknown;
  serAmount?: unknown;
  serCategory?: unknown;
  serNote?: unknown;
}

export type StaffTaskRecord = {
  name?: unknown;
  avatar?: unknown;
} & Record<string, unknown>;

export interface CatalogService {
  id?: unknown;
  serTitle?: unknown;
  serCategory?: unknown;
}

export interface PendingPaymentItem {
  recordIndex: number;
  taskKey: string;
  columnLabel: string;
  staffName: string;
  staffAvatar: string;
  guestLabel: string;
  serTitle: string;
  serNote?: string;
  task: TaskCell;
}

export interface ActiveTaskCell {
  recordIndex?: number;
  taskKey?: string;
}

function asText(value: unknown): string {
  return String(value ?? "").trim();
}

function isUnpaidAmount(amount: unknown): boolean {
  if (amount == null || amount === "") return true;
  const value = Number(amount);
  if (Number.isNaN(value)) return true;
  return value <= 0;
}

function isSystemCategory(serCategory: unknown): boolean {
  if (typeof serCategory === "string") {
    return serCategory.trim().toLowerCase() === "system";
  }
  if (!Array.isArray(serCategory)) return false;
  return serCategory.some(
    (category) => String(category).trim().toLowerCase() === "system"
  );
}

function isTaskCell(value: unknown): value is TaskCell {
  return value != null && typeof value === "object";
}

function buildSystemCatalogLookup(catalogServices: readonly CatalogService[]) {
  const ids = new Set<number>();
  const titles = new Set<string>();

  catalogServices.forEach((service) => {
    if (!isSystemCategory(service?.serCategory)) return;
    const id = Number(service.id);
    if (Number.isFinite(id)) ids.add(id);
    const title = asText(service.serTitle).toLowerCase();
    if (title) titles.add(title);
  });

  return { ids, titles };
}

function isSystemService(
  task: TaskCell,
  catalogLookup: { ids: Set<number>; titles: Set<string> }
): boolean {
  if (isSystemCategory(task.serCategory)) return true;
  const id = Number(task.id);
  if (Number.isFinite(id) && catalogLookup.ids.has(id)) return true;
  const title = asText(task.serTitle).toLowerCase();
  if (title && catalogLookup.titles.has(title)) return true;
  return false;
}

export function getPendingPayments(
  taskRecord: StaffTaskRecord[] | null | undefined,
  catalogServices: readonly CatalogService[] = []
): PendingPaymentItem[] {
  if (!Array.isArray(taskRecord) || taskRecord.length === 0) return [];

  const catalogLookup = buildSystemCatalogLookup(catalogServices);
  const items: PendingPaymentItem[] = [];

  taskRecord.forEach((record, recordIndex) => {
    const staffName = asText(record?.name);
    const staffAvatar = asText(record?.avatar);

    for (let i = 1; i <= TASK_SLOT_COUNT; i += 1) {
      const taskKey = `t${i}`;
      const task = record?.[taskKey];
      if (!isTaskCell(task)) continue;

      const serTitle = asText(task.serTitle);
      if (!serTitle || serTitle === "RESET") continue;
      if (isSystemService(task, catalogLookup)) continue;
      if (!isUnpaidAmount(task.serAmount)) continue;

      const serNote = asText(task.serNote);

      items.push({
        recordIndex,
        taskKey,
        columnLabel: `T${i}`,
        staffName,
        staffAvatar,
        // serDescription is a catalog subtitle (e.g. Request → "All in 1"), not a person name.
        guestLabel: staffName,
        serTitle,
        serNote: serNote || undefined,
        task,
      });
    }
  });

  return items;
}
